/**
 * SeeMe Places Typeahead
 * Google Places address autocomplete that attaches to any text input.
 *
 * Uses the modern AutocompleteSuggestion API (not legacy Autocomplete,
 * not PlaceAutocompleteElement). Renders its own dropdown portaled to
 * <body> so it escapes sheet/modal stacking contexts.
 *
 * API:
 *   SeeMe.placesTypeahead.attach(input, {
 *       apiKey: 'AIza...',
 *       countries: ['us'],
 *       types: ['street_address'],   // Places API (New) primary types; 'address' is rejected
 *       minChars: 3,
 *       debounceMs: 200,
 *       unitFallback: true,          // geocode unit-shaped queries autocomplete can't match
 *       onSelect: function(result) { },
 *       onError: function(err) { }
 *   })
 *
 * Unit fallback: autocomplete only offers a condo/apartment unit when the
 * query already matches Google's own spelling of it ("Apt 1701", "unit 104").
 * The Geocoding API is looser — "127 Madison Ave 1701 Memphis" geocodes to
 * "127 Madison Ave Apt 1701" with a `subpremise` component. When a query looks
 * like it carries a unit and no suggestion is a subpremise, the geocoded
 * subpremise is added as the first dropdown row; picking it runs the same
 * place-details path as any other suggestion.
 *
 *   SeeMe.placesTypeahead.detach(input)
 *
 * Selected results include displayName, types, and formattedAddress separately.
 * Use types to distinguish named venues from street addresses before combining
 * a name with an address; prediction labels can omit the street number.
 */

var SeeMe = window.SeeMe || {};

SeeMe.placesTypeahead = (function () {
    'use strict';

    var attached = new WeakMap();
    var loaderPromise = null;

    // ── Lazy-load Google Maps JS ────────────────────────────────────────

    function loadMapsApi(apiKey) {
        if (loaderPromise) return loaderPromise;
        if (window.google && window.google.maps && window.google.maps.places) {
            loaderPromise = Promise.resolve();
            return loaderPromise;
        }
        loaderPromise = new Promise(function (resolve, reject) {
            var script = document.createElement('script');
            script.src = 'https://maps.googleapis.com/maps/api/js?key=' +
                encodeURIComponent(apiKey) + '&libraries=places&v=weekly';
            script.async = true;
            script.onload = resolve;
            script.onerror = function () {
                loaderPromise = null;
                reject(new Error('Failed to load Google Maps JS'));
            };
            document.head.appendChild(script);
        });
        return loaderPromise;
    }

    // ── Parse address components from Place ─────────────────────────────

    function parsePlace(place) {
        var byType = {};
        if (place.addressComponents) {
            place.addressComponents.forEach(function (c) {
                c.types.forEach(function (t) { byType[t] = c; });
            });
        }

        var streetNumber = byType.street_number
            ? (byType.street_number.shortText || byType.street_number.longText || '') : '';
        // `shortText` is the abbreviated street type ("Anchor Dr", not "Anchor
        // Drive"). It matches both Google's own formattedAddress and the county
        // assessor convention we dedupe properties against — taking longText
        // here silently created duplicate property rows. `shortText` isn't
        // guaranteed present on every result, so fall back to `longText`
        // rather than writing the literal string "undefined" into the form.
        var route = byType.route ? (byType.route.shortText || byType.route.longText || '') : '';
        var address = streetNumber ? (streetNumber + ' ' + route) : route;

        if (byType.subpremise) {
            var unit = byType.subpremise.shortText || byType.subpremise.longText || '';
            // Google sometimes bakes the designator into the value itself
            // ("unit 18f", "Apt #3"), which would render as "Unit unit 18f".
            unit = String(unit)
                .replace(/^\s*(?:(?:units?|apt|apartment|ste|suite|no)\b\.?\s*)?#?\s*/i, '')
                .trim();
            // Short designators read as uppercase ("18f" → "18F"); leave
            // anything wordier alone.
            if (/^[a-z0-9-]{1,6}$/i.test(unit)) unit = unit.toUpperCase();
            if (unit) address += ' Unit ' + unit;
        }

        // Unincorporated areas get no `locality` at all — Google returns the
        // place name only as `neighborhood` (e.g. Cordova, TN 38016, whose
        // formattedAddress reads "Cordova, TN 38016"). The later fallbacks
        // fire only in that case, so a dense-city neighborhood like SoHo can
        // never hijack the city: it always carries a locality ("New York").
        var cityTypes = [
            'locality',
            'postal_town',
            'sublocality_level_1',
            'neighborhood',
            'administrative_area_level_3'
        ];
        // Accept a component only if it actually carries a value — testing for
        // the component alone stops the chain on a present-but-empty
        // `locality`, so `postal_town` / `neighborhood` /
        // `administrative_area_level_3` never get their turn.
        var city = '';
        for (var ci = 0; ci < cityTypes.length; ci++) {
            var cityComponent = byType[cityTypes[ci]];
            var cityValue = cityComponent
                ? (cityComponent.longText || cityComponent.shortText || '') : '';
            if (cityValue) {
                city = cityValue;
                break;
            }
        }

        var state = byType.administrative_area_level_1
            ? (byType.administrative_area_level_1.shortText || byType.administrative_area_level_1.longText || '') : '';
        var zipCode = byType.postal_code
            ? (byType.postal_code.shortText || byType.postal_code.longText || '') : '';
        // Guarded, not bare: parsePlace runs inside a .then(), so a TypeError
        // on a missing `longText` is swallowed by the .catch() below — the
        // dropdown closes and *no* field gets filled. Clicking a suggestion
        // would appear to do nothing, which is worse than one empty county.
        var county = byType.administrative_area_level_2
            ? String(byType.administrative_area_level_2.longText || '')
                .replace(/\s*County\s*$/i, '') : '';

        return {
            address: address,
            city: city,
            state: state,
            zipCode: zipCode,
            county: county,
            lat: place.location ? place.location.lat() : null,
            lng: place.location ? place.location.lng() : null,
            formattedAddress: place.formattedAddress || '',
            displayName: place.displayName || '',
            types: place.types || [],
            placeId: place.id || '',
            raw: place
        };
    }

    // ── Helpers ─────────────────────────────────────────────────────────

    function escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    var UNIT_DESIGNATOR_RE = /(?:^|[\s,])(?:(?:unit|apt|apartment|ste|suite)\b\.?\s*#?\s*|#\s*)[a-z0-9-]+/i;
    var ZIP_RE = /^\d{5}(?:-\d{4})?$/;

    // A query that probably names a unit: an explicit designator ("apt 1701",
    // "#104"), or a second bare number after the house number that is not a
    // ZIP ("127 Madison Ave 1701 Memphis"). Ordinals ("5th") don't count. The
    // word designators require a word boundary before what follows, so a
    // plain word like "Stevens" or "Unity" that merely starts with one of
    // them doesn't match.
    function looksLikeUnitQuery(query) {
        if (UNIT_DESIGNATOR_RE.test(query)) return true;
        var numbers = query.match(/\b\d+\b/g) || [];
        return numbers.slice(1).some(function (n) { return !ZIP_RE.test(n); });
    }

    function hasSubpremise(suggestions) {
        return suggestions.some(function (s) {
            var types = s.placePrediction && s.placePrediction.types;
            return !!types && types.indexOf('subpremise') !== -1;
        });
    }

    function positionDropdown(dropdown, input) {
        var rect = input.getBoundingClientRect();
        var MARGIN = 8;

        dropdown.style.left = rect.left + 'px';
        dropdown.style.width = rect.width + 'px';

        var dropdownHeight = dropdown.offsetHeight;
        if (rect.bottom + dropdownHeight + MARGIN > window.innerHeight) {
            dropdown.style.top = (rect.top - dropdownHeight - 4) + 'px';
        } else {
            dropdown.style.top = (rect.bottom + 4) + 'px';
        }
    }

    // ── Attach ──────────────────────────────────────────────────────────

    function attach(input, opts) {
        if (!input || !opts || !opts.apiKey) return;
        if (attached.has(input)) return;

        var defaults = {
            countries: ['us'],
            types: [],
            minChars: 3,
            debounceMs: 200,
            unitFallback: true,
            onSelect: null,
            onError: function (err) { console.warn('[places-typeahead]', err); }
        };
        var cfg = {};
        var key;
        for (key in defaults) cfg[key] = defaults[key];
        for (key in opts) if (opts.hasOwnProperty(key)) cfg[key] = opts[key];

        // Sweep dropdowns orphaned by a torn-down sheet/modal. Their owning input
        // is already gone, so detach() can never be reached for them. Anything
        // still `.show` belongs to a live input on this page — leave it.
        document.querySelectorAll('body > .places-typeahead-dropdown:not(.show)')
            .forEach(function (stale) { stale.remove(); });

        // Portaled to <body> only while open (see showDropdown/hideDropdown) so a
        // sheet that closes without a blur can't strand it there.
        var dropdown = document.createElement('div');
        dropdown.className = 'places-typeahead-dropdown';

        var state = {
            cfg: cfg,
            dropdown: dropdown,
            sessionToken: null,
            debounceTimer: null,
            focusedIndex: -1,
            results: [],
            // Bumped per keystroke so a slow autocomplete or geocode response
            // for an earlier query cannot paint over a newer one.
            requestSeq: 0,
            bound: false
        };

        var listeners = {};
        var justSelected = false;

        function newSessionToken() {
            state.sessionToken = new google.maps.places.AutocompleteSessionToken();
        }

        function showDropdown(html) {
            dropdown.innerHTML = html;
            // Must be in the DOM before positionDropdown reads offsetHeight.
            if (!dropdown.isConnected) document.body.appendChild(dropdown);
            dropdown.classList.add('show');
            positionDropdown(dropdown, input);
        }

        function hideDropdown() {
            dropdown.classList.remove('show');
            dropdown.remove();
            dropdown.innerHTML = '';
            state.focusedIndex = -1;
            state.results = [];
        }

        function setFocused(index) {
            var items = dropdown.querySelectorAll('.dropdown-item');
            items.forEach(function (el) { el.classList.remove('focused'); });
            state.focusedIndex = index;
            if (index >= 0 && index < items.length) {
                items[index].classList.add('focused');
                items[index].scrollIntoView({ block: 'nearest' });
            }
        }

        // `state.results` holds autocomplete suggestions plus, after the unit
        // fallback, entries shaped `{ geocoded: GeocoderResult }`.
        function renderResults() {
            var results = state.results;
            if (!results.length) {
                showDropdown(
                    '<div class="places-typeahead-empty">No addresses found</div>'
                );
                return;
            }

            var html = results.map(function (s, i) {
                var main = '';
                var secondary = '';
                if (s.geocoded) {
                    main = escapeHtml(String(s.geocoded.formatted_address || '').replace(/,\s*USA$/, ''));
                } else {
                    var pred = s.placePrediction;
                    main = (pred.mainText && pred.mainText.text) ? escapeHtml(pred.mainText.text) : '';
                    secondary = (pred.secondaryText && pred.secondaryText.text) ? escapeHtml(pred.secondaryText.text) : '';
                    if (!main && pred.text && pred.text.text) main = escapeHtml(pred.text.text);
                }
                return '<a class="dropdown-item" href="#" data-places-index="' + i + '">' +
                    main +
                    (secondary ? ' <span class="text-muted">' + secondary + '</span>' : '') +
                    '</a>';
            }).join('');

            var focused = state.focusedIndex;
            showDropdown(html);
            // A fallback row added while the user was arrowing through the
            // list must not reset their position.
            if (focused >= 0) setFocused(focused); else state.focusedIndex = -1;
        }

        function fetchAndRender(query) {
            if (!state.sessionToken) newSessionToken();
            var seq = ++state.requestSeq;

            showDropdown(
                '<div class="places-typeahead-loading">' +
                '<div class="spinner spinner--sm spinner--muted"></div></div>'
            );

            var request = {
                input: query,
                sessionToken: state.sessionToken,
                includedRegionCodes: cfg.countries
            };
            if (cfg.types && cfg.types.length) {
                request.includedPrimaryTypes = cfg.types;
            }

            google.maps.places.AutocompleteSuggestion
                .fetchAutocompleteSuggestions(request)
                .then(function (response) {
                    if (seq !== state.requestSeq) return;
                    state.results = response.suggestions || [];
                    state.focusedIndex = -1;
                    renderResults();

                    if (cfg.unitFallback && looksLikeUnitQuery(query) && !hasSubpremise(state.results)) {
                        geocodeUnit(query, seq);
                    }
                })
                .catch(function (err) {
                    if (seq !== state.requestSeq) return;
                    hideDropdown();
                    if (cfg.onError) cfg.onError(err);
                });
        }

        // Ask the geocoder for the unit autocomplete could not match, and put it
        // above the autocomplete rows. Advisory: a miss or an error leaves the
        // dropdown exactly as autocomplete rendered it.
        function geocodeUnit(query, seq) {
            var request = { address: query };
            if (cfg.countries && cfg.countries.length === 1) {
                request.region = cfg.countries[0];
            }
            new google.maps.Geocoder().geocode(request)
                .then(function (response) {
                    if (seq !== state.requestSeq) return;
                    if (!dropdown.classList.contains('show')) return;
                    var match = (response.results || []).filter(function (r) {
                        return r.place_id && r.types && r.types.indexOf('subpremise') !== -1;
                    })[0];
                    if (!match) return;
                    // First row: for a unit query the autocomplete rows are
                    // same-number streets in other states. Keep a highlighted
                    // row highlighted by shifting the index with it.
                    state.results = [{ geocoded: match }].concat(state.results);
                    if (state.focusedIndex >= 0) state.focusedIndex += 1;
                    renderResults();
                })
                .catch(function () { /* fall back to the autocomplete rows */ });
        }

        function selectByIndex(index) {
            var suggestion = state.results[index];
            if (!suggestion) return;

            // A geocoded row carries only a place id; the details fetch below
            // gives it the same Place shape as an autocomplete pick.
            var place = suggestion.geocoded
                ? new google.maps.places.Place({ id: suggestion.geocoded.place_id })
                : suggestion.placePrediction.toPlace();
            place.fetchFields({
                fields: ['addressComponents', 'location', 'formattedAddress', 'id', 'displayName', 'types']
            }).then(function (resp) {
                var result = parsePlace(resp.place);
                var prediction = suggestion.placePrediction;
                var predictionText = prediction && prediction.text;
                // Keep Google's complete label: combining mainText with the
                // formatted address duplicates differently abbreviated streets.
                result.description = (predictionText ? predictionText.toString() : '')
                    || result.formattedAddress || '';
                hideDropdown();
                newSessionToken();
                justSelected = true;
                if (cfg.onSelect) cfg.onSelect(result);
            }).catch(function (err) {
                hideDropdown();
                if (cfg.onError) cfg.onError(err);
            });
        }

        // ── Event listeners ─────────────────────────────────────────────

        listeners.input = function () {
            if (justSelected) { justSelected = false; return; }
            var q = input.value.trim();
            clearTimeout(state.debounceTimer);

            if (q.length < cfg.minChars) {
                hideDropdown();
                return;
            }

            state.debounceTimer = setTimeout(function () {
                fetchAndRender(q);
            }, cfg.debounceMs);
        };

        listeners.keydown = function (e) {
            if (!dropdown.classList.contains('show')) return;

            var items = dropdown.querySelectorAll('.dropdown-item');
            if (!items.length && e.key !== 'Escape') return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setFocused(state.focusedIndex < items.length - 1 ? state.focusedIndex + 1 : 0);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocused(state.focusedIndex > 0 ? state.focusedIndex - 1 : items.length - 1);
            } else if (e.key === 'Enter') {
                if (state.focusedIndex >= 0) {
                    e.preventDefault();
                    selectByIndex(state.focusedIndex);
                }
            } else if (e.key === 'Escape') {
                e.stopPropagation();  // close the list, not the sheet or modal under it
                hideDropdown();
            }
        };

        listeners.dropdownClick = function (e) {
            var item = e.target.closest('[data-places-index]');
            if (!item) return;
            e.preventDefault();
            selectByIndex(parseInt(item.getAttribute('data-places-index'), 10));
        };

        listeners.clickOutside = function (e) {
            if (!dropdown.classList.contains('show')) return;
            if (dropdown.contains(e.target) || e.target === input) return;
            hideDropdown();
        };

        listeners.blur = function () {
            setTimeout(function () {
                if (!dropdown.contains(document.activeElement)) {
                    hideDropdown();
                }
            }, 150);
        };

        listeners.reposition = function () {
            if (dropdown.classList.contains('show')) {
                positionDropdown(dropdown, input);
            }
        };

        // Hover highlights
        listeners.mouseOver = function (e) {
            var item = e.target.closest('[data-places-index]');
            if (!item) return;
            var items = dropdown.querySelectorAll('.dropdown-item');
            items.forEach(function (el) { el.classList.remove('focused'); });
            item.classList.add('focused');
            state.focusedIndex = parseInt(item.getAttribute('data-places-index'), 10);
        };

        // ── Wire up after Maps loads ────────────────────────────────────

        function bindListeners() {
            if (state.bound) return;
            state.bound = true;
            newSessionToken();

            input.addEventListener('input', listeners.input);
            input.addEventListener('keydown', listeners.keydown);
            input.addEventListener('blur', listeners.blur);
            dropdown.addEventListener('click', listeners.dropdownClick);
            dropdown.addEventListener('mouseover', listeners.mouseOver);
            document.addEventListener('click', listeners.clickOutside);
            window.addEventListener('scroll', listeners.reposition, true);
            window.addEventListener('resize', listeners.reposition);
        }

        state.listeners = listeners;
        attached.set(input, state);

        loadMapsApi(opts.apiKey).then(function () {
            if (!attached.has(input)) return;
            bindListeners();
        }).catch(function (err) {
            if (cfg.onError) cfg.onError(err);
        });
    }

    // ── Detach ──────────────────────────────────────────────────────────

    function detach(input) {
        if (!input || !attached.has(input)) return;
        var state = attached.get(input);
        var listeners = state.listeners;

        clearTimeout(state.debounceTimer);

        if (state.bound) {
            input.removeEventListener('input', listeners.input);
            input.removeEventListener('keydown', listeners.keydown);
            input.removeEventListener('blur', listeners.blur);
            state.dropdown.removeEventListener('click', listeners.dropdownClick);
            state.dropdown.removeEventListener('mouseover', listeners.mouseOver);
            document.removeEventListener('click', listeners.clickOutside);
            window.removeEventListener('scroll', listeners.reposition, true);
            window.removeEventListener('resize', listeners.reposition);
        }

        if (state.dropdown.parentNode) {
            state.dropdown.parentNode.removeChild(state.dropdown);
        }

        attached.delete(input);
    }

    return { attach: attach, detach: detach };

})();

window.SeeMe = SeeMe;
